import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

import { AudioProvider } from "@/context/AudioContext";
import Preloader from "@/components/Preloader";
import Hero from "@/components/Hero";
import BrandMarquee from "@/components/BrandMarquee";
import Tracklist from "@/components/Tracklist";
import StreamingLinks from "@/components/StreamingLinks";
import Merch from "@/components/Merch";
import Manifesto from "@/components/Manifesto";
import GuestBook from "@/components/GuestBook";
import PlayerBar from "@/components/PlayerBar";
import DownloadDialog from "@/components/DownloadDialog";
import Footer from "@/components/Footer";
import StickyBuyBar from "@/components/StickyBuyBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const UNLOCK_KEY = "fre3brd_unlocked";

export default function Home() {
  const [data, setData] = useState(null);
  const [ready, setReady] = useState(false);
  const [unlocked, setUnlocked] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(UNLOCK_KEY) === "1"
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}/tracks`);
        const payload = {
          ...res.data,
          tracks: (res.data.tracks || []).map((t) => ({
            ...t,
            src: t.src && t.src.startsWith("http") ? t.src : `${BACKEND_URL}${t.src}`,
          })),
        };
        if (!cancelled) setData(payload);
      } catch (e) {
        if (attempt < 4 && !cancelled) {
          setTimeout(() => load(attempt + 1), 700);
        } else {
          console.error("Failed to load tracks", e);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => setReady(true), 2400);
    return () => clearTimeout(t);
  }, [data]);

  useEffect(() => {
    if (!ready) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    if (typeof window !== "undefined") window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, [ready]);

  const downloadTrack = useCallback(async (track) => {
    const id = toast.loading(`Preparing "${track.title}"…`);
    try {
      const res = await fetch(track.src);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = track.download_name || `${track.title}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded "${track.title}"`, { id });
    } catch (e) {
      window.open(track.src, "_blank");
      toast.message(`Opening "${track.title}" — right-click to save`, { id });
    }
  }, []);

  const downloadAll = useCallback(async () => {
    if (!data) return;
    setDialogOpen(false);
    toast.message("Starting full tape download…");
    for (const track of data.tracks) {
      // eslint-disable-next-line no-await-in-loop
      await downloadTrack(track);
    }
  }, [data, downloadTrack]);

  const handleSubmit = useCallback(async (email) => {
    const res = await axios.post(`${API}/subscribe`, { email });
    if (res.data && res.data.success) {
      setUnlocked(true);
      localStorage.setItem(UNLOCK_KEY, "1");
      toast.success("Download unlocked. Enjoy FRE3BRD.");
    }
  }, []);

  const openDialog = useCallback(() => setDialogOpen(true), []);

  if (!data) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-600 animate-pulse">
          Loading FRE3BRD…
        </div>
      </div>
    );
  }

  return (
    <AudioProvider tracks={data.tracks}>
      <div className="App bg-black min-h-screen text-white">
        <div className="noise-overlay" />

        <AnimatePresence>
          {!ready && <Preloader key="pre" onDone={() => setReady(true)} />}
        </AnimatePresence>

        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          transition={{ duration: 0.6 }}
        >
          <Hero cover={data.cover_art} onDownload={openDialog} />
          <BrandMarquee />
          <Tracklist
            tracks={data.tracks}
            unlocked={unlocked}
            onDownload={openDialog}
            onDownloadTrack={downloadTrack}
          />
          <StreamingLinks links={data.streaming} />
          <Merch />
          <GuestBook />
          <Manifesto />
          <Footer />
        </motion.main>

        <PlayerBar />
        <StickyBuyBar />
        <DownloadDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleSubmit}
          unlocked={unlocked}
          onDownloadAll={downloadAll}
        />
      </div>
    </AudioProvider>
  );
}
